<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<div class="p-fluid">

  <app-warehousing-page-header activeSection="stock-entry" title="Goods In"
    subtitle="Receive one or more items into a warehouse and issue a goods receipt note.">
    <div actions class="flex gap-2">
      <p-button label="Scanner Receive" icon="pi pi-barcode" severity="secondary" [outlined]="true" routerLink="/warehousing/stock-entry/scan"></p-button>
      <p-button label="Single Item" icon="pi pi-plus" severity="secondary" [outlined]="true" routerLink="/warehousing/stock-entry/add"></p-button>
      <p-button label="New Goods In" icon="pi pi-sign-in" routerLink="/warehousing/stock-entry/bulk-receive"></p-button>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadStockEntries()"></p-button>
    </div>
  </app-warehousing-page-header>

  <app-warehousing-search-panel title="Search Receive Stock" [resultCount]="entries.length" [totalCount]="entries.length" (search)="applySearch()" (clear)="clearSearch()">
    <div [formGroup]="searchForm" class="formgrid grid">
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon><p-iftaLabel><input id="stockEntryKeyword" pInputText formControlName="keyword"><label for="stockEntryKeyword">Keyword</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon><p-iftaLabel><p-select id="stockEntryWarehouse" [options]="masterData.WAREHOUSE" optionLabel="name" optionValue="id" formControlName="warehouseId" [showClear]="true" styleClass="w-full"></p-select><label for="stockEntryWarehouse">Warehouse</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-box"></i></p-inputGroupAddon><p-iftaLabel><p-autoComplete inputId="stockEntryItem" formControlName="item" [suggestions]="itemOptions" optionLabel="displayLabel" [forceSelection]="true" [showClear]="true" [delay]="300" [minLength]="1" appendTo="body" styleClass="w-full" (completeMethod)="searchItemOptions($event)"></p-autoComplete><label for="stockEntryItem">Item</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-4">
        <div class="grid">
          <div class="field col-6 mb-0">
            <p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon><p-iftaLabel><input id="stockEntryReceivedFrom" pInputText type="date" formControlName="receivedFrom"><label for="stockEntryReceivedFrom">From</label></p-iftaLabel></p-inputGroup>
          </div>
          <div class="field col-6 mb-0">
            <p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar-times"></i></p-inputGroupAddon><p-iftaLabel><input id="stockEntryReceivedTo" pInputText type="date" formControlName="receivedTo"><label for="stockEntryReceivedTo">To</label></p-iftaLabel></p-inputGroup>
          </div>
        </div>
      </div>
    </div>
  </app-warehousing-search-panel>

  <section class="warehousing-table-card">
    <div class="warehousing-table-header">
      <div>
        <h2>Stock Receipt Directory</h2>
        <p>One row per GRN, ordered by newest receipt date. Every received item is shown below.</p>
      </div>
      <span class="warehousing-result-chip">{{ entries.length }} records</span>
    </div>
    <p-table [value]="entries" [paginator]="true" [rows]="12" [scrollable]="true" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} receipts" [rowsPerPageOptions]="[12, 25, 50]" [tableStyle]="{ 'min-width': '68rem' }">
      <ng-template pTemplate="header">
        <tr><th>Receipt</th><th>Source &amp; Warehouse</th><th>Items</th><th class="text-right">Total Received</th><th>Audit</th><th class="text-center">Actions</th></tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>
            <div class="stack-cell">
              <span class="warehousing-code-pill">{{ row.receiptNumber || '-' }}</span>
              <div class="cell-meta vertical">
                <span><i class="pi pi-calendar"></i> {{ displayDate(row.receiptDate) }}</span>
                <span><i class="pi pi-file"></i> {{ row.referenceNumber || 'No external reference' }}</span>
                <span class="receipt-evidence" *ngIf="row.notes || row.attachments?.length">
                  <span *ngIf="row.notes" title="Receipt has notes" aria-label="Receipt has notes"><i class="pi pi-align-left"></i> Notes</span>
                  <span *ngIf="row.attachments?.length" [title]="(row.attachments?.length || 0) + ' delivery note attachments'" [attr.aria-label]="(row.attachments?.length || 0) + ' delivery note attachments'"><i class="pi pi-paperclip"></i> {{ row.attachments?.length }} DN</span>
                </span>
              </div>
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <span class="cell-title" *ngIf="row.supplierName">{{ row.supplierName }}</span>
              <span class="text-500" *ngIf="!row.supplierName">-</span>
              <span class="cell-title">{{ row.warehouseName || masterName('WAREHOUSE', row.warehouseId) }}</span>
              <div class="cell-meta vertical" *ngIf="row.driverName || row.vehiclePlateNumber">
                <span *ngIf="row.driverName"><i class="pi pi-user"></i> {{ row.driverName }}</span>
                <span *ngIf="row.vehiclePlateNumber"><i class="pi pi-car"></i> {{ row.vehiclePlateNumber }}</span>
              </div>
            </div>
          </td>
          <td class="receipt-lines-cell">
            <ul class="receipt-line-preview" *ngIf="row.lineSummaries?.length; else noReceiptLines">
              <li *ngFor="let line of row.lineSummaries">
                <div class="receipt-line-heading">
                  <span class="receipt-line-name">{{ receiptLineName(line) }}</span>
                  <strong class="receipt-line-quantity">Qty {{ line.quantity | number:'1.0-4' }}</strong>
                </div>
                <span class="receipt-line-size">Size: {{ receiptLineSize(line) }}</span>
              </li>
            </ul>
            <ng-template #noReceiptLines><span class="text-500">No receipt lines</span></ng-template>
          </td>
          <td class="text-right">
            <div class="stack-cell number-stack">
              <div class="quality-pill">{{ row.totalQuantity | number:'1.0-4' }}</div>
              <span class="text-500">Base quantity</span>
            </div>
          </td>
          <td><app-audit-directory-cell [stamp]="row"></app-audit-directory-cell></td>
          <td class="text-center">
            <div class="flex gap-1 justify-content-center align-items-center">
              <p-button *ngIf="row.id" label="Details" icon="pi pi-eye" size="small" severity="secondary" [outlined]="true"
                [routerLink]="['/warehousing/stock-entry/view', row.id]"></p-button>
              <p-button *ngIf="row.id" label="GRN" icon="pi pi-file-pdf" size="small" severity="success"
                [outlined]="true" [title]="'Download Goods Receipt Note (GRN) PDF'"
                (click)="downloadGrn(row.id, row.receiptNumber)"></p-button>
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="6"><div class="warehousing-empty-state"><i class="pi pi-search"></i><strong>No stock receipts found</strong><span>No matching receipt records.</span></div></td></tr></ng-template>
    </p-table>
  </section>

</div>
